'use client';

import { MenuSquare, XSquare } from 'lucide-react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';

import { Image } from '@/components/image';
import { Link } from '@/components/link';
import { VisuallyHidden } from '@/components/visually-hidden';
import { mobileAppNavIsOpenState } from '@/state/navigation';
import { tv } from '@/utils/styles';

interface Props {
  className?: string;
}

const AppNavigationHeader = ({ className }: Props) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [mobileNavIsOpen, setMobileNavIsOpen] = useRecoilState(mobileAppNavIsOpenState);

  useEffect(() => {
    // Any time the route changes, close mobile nav
    setMobileNavIsOpen(false);
  }, [pathname, searchParams]);

  const { base, button, hamburger, close, toggleText, openText, closeText } = styles({ mobileNavIsOpen });

  return (
    <div className={base({ className })}>
      <Link href="/">
        <Image src="/assets/icons/logo-colored-dark.svg" alt="Fluent Cargo Logo" />
        <VisuallyHidden>Go to home page</VisuallyHidden>
      </Link>
      <button onClick={() => setMobileNavIsOpen(!mobileNavIsOpen)} type="button" className={button()}>
        <span className={toggleText()}>
          <span className={openText()}>Menu</span>
          <span className={closeText()}>Close</span>
        </span>

        <XSquare className={close()} />
        <MenuSquare className={hamburger()} />
      </button>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'fixed top-0 z-40 flex h-16 w-full justify-between bg-blue-800 p-4 md:hidden',
    button: 'absolute right-2 top-1/2 h-11 w-24 -translate-y-1/2',
    hamburger: 'absolute right-2 top-1/2 -translate-y-1/2 text-lightBlue-500 transition-all duration-300',
    close: 'absolute right-2 top-1/2 -translate-y-1/2 text-lightBlue-500 opacity-0 transition-all duration-300',
    toggleText: 'absolute right-10 top-1/2 h-5 w-12 -translate-y-1/2 overflow-hidden text-white',
    openText: 'absolute right-0 transition-all duration-300',
    closeText: 'absolute right-0 transition-all duration-300',
  },
  variants: {
    mobileNavIsOpen: {
      true: {
        hamburger: 'scale-50 opacity-0',
        close: 'scale-100 opacity-100',
        openText: '-translate-y-6 opacity-0',
        closeText: 'scale-100 opacity-100',
      },
      false: {
        hamburger: 'scale-100 opacity-100',
        close: 'scale-50 opacity-0',
        openText: 'scale-100 opacity-100',
        closeText: 'translate-y-6 opacity-0',
      },
    },
  },
});

export { AppNavigationHeader };
